import moment from "moment";
import { useMemo } from "react";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import filterFactory, {
  selectFilter,
  textFilter,
} from "react-bootstrap-table2-filter";
import paginationFactory from "react-bootstrap-table2-paginator";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import { AiFillEye } from "react-icons/ai";
import { ImDownload3 } from "react-icons/im";
import { FactureEmise, ModelDevis } from "../../../../redux/types";
import { getLastModel } from "../../../../utils/api/user.api";
import { formatAmout } from "../../../../utils/formatter";
import { useDefaultPaginationOptions } from "../../../guest/hooks";
import DetailFactureEmiseComptabiliteModal from "../../../modal/comptabilite/DetailFactureEmiseComptabiliteModal";
import ListeFactureComtabiliteTableSkeleton from "./ListeFactureComtabiliteTableSkeleton";
import {
  FACTURE_BCS_FILTER_OPTIONS,
  getFactureBcsInfo,
  HORS_BCS,
} from "../../../../utils/bcs-status";
import { useDocumentsDownload } from "../../../../utils/download-documents";
import { useRowSelection } from "../../../../utils/useRowSelection";
import BcsStatusBadge from "../../../common/BcsStatusBadge";
import SelectionBar from "../../../common/SelectionBar";

// L'entité légale sous laquelle la facture a été émise. Elle s'appelle
// « model » dans la base et dans les types, d'où les identifiants de ce
// fichier ; « entité légale » est le terme montré à l'utilisateur.
// Le nom commercial est ce que le client voit sur le document ; les replis
// couvrent les entités anciennes, créées sans nom commercial.
function modelLabel(model: ModelDevis) {
  return model.nom_commercial || model.nom_exploitant || model.siret || model.id;
}

function ListeFactureEmiseComtabiliteTable({
  invoices,
  isLoading,
  reload,
  userId,
  models,
}: {
  invoices: FactureEmise[];
  isLoading: boolean;
  reload: () => any;
  userId?: string;
  // Entités légales de l'utilisateur affiché. Absentes, la colonne « Entité
  // légale » ne s'affiche pas : les vues qui listent les factures de TOUS les
  // utilisateurs n'ont pas de jeu d'entités pertinent.
  models?: ModelDevis[];
}) {
  const readOnly = !userId;

  const modelOptions = useMemo(() => {
    const options: { [id: string]: string } = {};
    for (const m of models ?? []) options[m.id] = modelLabel(m);
    return options;
  }, [models]);
  const hasModels = Object.keys(modelOptions).length > 0;

  // Près d'une facture sur deux n'a pas de modelId (relevé en production).
  // Partout ailleurs — génération du PDF, pages publiques — ces factures sont
  // rendues avec la DERNIÈRE entité légale de l'utilisateur. Le filtre
  // applique la même règle, sinon il écarterait la moitié des factures de
  // l'entité principale alors que c'est bien elle qui figure sur le document.
  const defaultModelId = useMemo(
    () => (models && models.length ? getLastModel(models)?.id : undefined),
    [models]
  );
  const effectiveModelId = (row: FactureEmise) =>
    row.modelId || defaultModelId || "";

  // Sélection multiple pour récupérer les PDF d'un coup : un seul document
  // donne le PDF, plusieurs donnent une archive zip.
  const { selected, selectedRows, selectRow, clear } =
    useRowSelection(invoices);
  const { isDownloading, download } = useDocumentsDownload();

  const downloadSelection = async () => {
    await download(
      selectedRows.map((f) => ({
        url: f.file,
        name: `${f.numero} ${f.nom_client ?? ""}`,
      })),
      `factures-emises-${moment().format("YYYY-MM-DD")}`
    );
    // La sélection est consommée par l'action, comme côté dépenses.
    clear();
  };

  const actionFormatter = (cell, row) => {
    return (
      <>
        <div className="table-actions-btn-container-commandes d-flex gap-3">
          <div
            className="modal fade"
            id={`DetailFactureEmiseComptabiliteModal${row.uid}`}
            aria-labelledby="DetailFactureEmiseComptabiliteModalLabel"
            aria-hidden="true"
          >
            <DetailFactureEmiseComptabiliteModal
              facture={row}
              func={reload}
              readOnly={readOnly}
              modalId={`DetailFactureEmiseComptabiliteModal${row.uid}`}
              btnId={`bntFactureComptabiliteModal${row.uid}`}
            />
          </div>

          <div className="container-btn-action-icon-modal">
            <button
              className="btn btn-action-modal-icon with-tooltip"
              data-tooltip-content="Voir"
              data-bs-toggle="modal"
              data-bs-target={`#DetailFactureEmiseComptabiliteModal${row?.uid}`}
              id={`bntFactureComptabiliteModal${row.uid}`}
            >
              <AiFillEye />
            </button>
          </div>
        </div>
      </>
    );
  };

  const options = useDefaultPaginationOptions();

  const STATUS_OPTIONS = {
    0: "À traiter",
    1: "Validé",
  };

  const columns = [
    {
      dataField: "numero",
      text: "Numéro",
      filter: textFilter({ placeholder: "Chercher..." }),
    },
    // {
    //   dataField: "statut",
    //   text: "Statut",
    //   filter: textFilter({ placeholder: "Chercher..." }),
    // },
    {
      dataField: "statut",
      text: "Statut",
      filterValue: (cell) => (cell === "Soldée" ? 1 : 0),
      filter: selectFilter({
        options: STATUS_OPTIONS,
        placeholder: "Choisir",
      }),
      formatter: (cell: any, row: FactureEmise) => {
        if (row?.statut === "Soldée") {
          return (
            <div className="table-actions-btn-container-statut-facture">
              <p className="statut-facture-valide mb-0">Validé</p>
            </div>
          );
        } else {
          return (
            <div className="table-actions-btn-container-statut-facture">
              <p className="statut-facture-a-traiter mb-0">À traiter</p>
            </div>
          );
        }
      },
    },
    {
      // Distinct de « Statut » : celui-ci est l'état de traitement COMPTABLE
      // (Validé / À traiter) ; celle-ci, le statut de la facture sur la
      // plateforme de facturation électronique, partagé avec le client.
      dataField: "bcsLifecycleStatus",
      text: "Facturation électronique",
      filterValue: (cell: any, row: FactureEmise) =>
        getFactureBcsInfo(row)?.label ?? HORS_BCS,
      filter: selectFilter({
        options: FACTURE_BCS_FILTER_OPTIONS,
        placeholder: "Choisir",
      }),
      formatter: (cell: any, row: FactureEmise) => (
        <BcsStatusBadge info={getFactureBcsInfo(row)} />
      ),
    },
    // {
    //   dataField: "numero_client",
    //   text: "N client",
    //   filter: textFilter({ placeholder: "Chercher..." }),
    // },
    {
      dataField: "prenom_client",
      filterValue: (cell, row) => `${row.prenom_client} ${row.nom_client}`,
      text: "Client",
      formatter: (cell: any, row: any) => {
        return `${row.prenom_client} ${row.nom_client}`;
      },
      filter: textFilter({ placeholder: "Chercher..." }),
    },
    // Colonne conditionnelle : un utilisateur qui émet sous plusieurs entités
    // légales a besoin d'isoler les factures de l'une d'elles.
    ...(hasModels
      ? [
          {
            dataField: "modelId",
            text: "Entité légale",
            filterValue: (cell: any, row: FactureEmise) => effectiveModelId(row),
            filter: selectFilter({
              options: modelOptions,
              placeholder: "Choisir",
            }),
            formatter: (cell: any, row: FactureEmise) =>
              modelOptions[effectiveModelId(row)] || "-",
          },
        ]
      : []),
    {
      dataField: "created_at",
      filterValue: (cell, row) => moment(cell).format("DD/MM/YYYY"),
      text: "Créer le",
      formatter: (cell: any, row: any) => {
        return moment(cell).format("DD/MM/YYYY");
      },
      filter: textFilter({ placeholder: "Chercher..." }),
      sort: true,
      sortFunc: (a, b, order, dataField, rowA, rowB) => {
        if (order === "desc") {
          return rowB.created_at - rowA.created_at;
        }
        return rowA.created_at - rowB.created_at;
      },
    },
    {
      dataField: "total_ht",
      text: "HT",
      formatter: (cell: any, row: any) => {
        return formatAmout(row.total_ht_after_remise);
      },
    },

    {
      dataField: "total_ttc",
      text: "TTC",
      formatter: (cell: any, row: any) => {
        return formatAmout(row.total_ttc);
      },
    },
    {
      dataField: "date_echeance",
      text: "Échéances",
      formatter: (cell: any, row: any) => {
        if (row.acoumpt || row.echeance1 || row.echeance2) return "OUI";
        return "NON";
      },
    },
    {
      dataField: "id",
      text: "Actions",
      style: { textAlign: "center" },
      formatter: (cell: any, row: any) => actionFormatter(cell, row),
    },
  ];

  return (
    <>
      {isLoading && <ListeFactureComtabiliteTableSkeleton />}
      {!isLoading && (
        <>
          <SelectionBar count={selected.length} onClear={clear}>
            <button
              type="button"
              className="btn btn-see-more"
              onClick={downloadSelection}
              disabled={isDownloading}
            >
              <ImDownload3 />
              <span className="ps-2">
                {selected.length > 1 ? "Télécharger (zip)" : "Télécharger"}
              </span>
            </button>
          </SelectionBar>
          <BootstrapTable
            keyField="uid"
            data={invoices}
            columns={columns}
            bordered={true}
            sort={{ dataField: "date", order: "desc" }}
            condensed={false}
            responsive={true}
            wrapperClasses="table-responsive admin-table"
            noDataIndication={() => "Il ya pas de données"}
            pagination={paginationFactory(options)}
            filter={filterFactory()}
            selectRow={selectRow}
          />
        </>
      )}
    </>
  );
}

export default ListeFactureEmiseComtabiliteTable;
